/**
 * Link-Design der HTML-Boxen — EINE Regel für Leser, Editor-Vorschau,
 * Pro-Editor-Bühne und das Eingabefeld der HTML-Box.
 *
 * Jan 06.10.2026: „Ich kann ja einen Link erzeugen, aber ich kann bisher nicht
 * die Farben dazu einstellen — die wichtigsten wäre Link-Farbe, Visited, Mouse
 * over.“ Entscheidungen: pro Scrolly (slideOptions.linkStyle, pro Slide über
 * deren eigene Optionen überschreibbar), ohne Wahl Textfarbe + Unterstrich,
 * Unterstreichung wählbar (immer / bei Mouse-over / nie).
 *
 * VORHER (gemessen) sah derselbe Link an vier Stellen verschieden aus:
 * Eingabefeld blau ohne Strich, Pro-Editor-Bühne in Textfarbe ohne Strich,
 * Editor-Vorschau in Textfarbe mit Strich, Leser Browser-Blau (besucht Lila)
 * mit Strich.
 *
 * Umgesetzt mit CSS-Variablen am Box-Element plus festen Regeln (linkRegeln):
 * Farben für besuchte Links lassen sich nur per Stylesheet setzen, nicht als
 * Inline-Stil am Link.
 */
import type { Color } from "./post.js";

/** Unterstreichung: immer (Vorgabe), nur bei Mouse-over, nie. */
export type LinkUnterstrich = "always" | "hover" | "never";

export interface LinkStyle {
    /** Linkfarbe; fehlt = Textfarbe der Box */
    color?: Color | null;
    /** Besuchte Links; fehlt = wie `color` */
    visitedColor?: Color | null;
    /** Mouse-over; fehlt = wie `color` */
    hoverColor?: Color | null;
    /** fehlt = immer */
    underline?: LinkUnterstrich | null;
}

const alsCss = (c: Color | null | undefined): string | undefined =>
    c && typeof c.r === "number" && typeof c.g === "number" && typeof c.b === "number"
        ? `rgba(${c.r}, ${c.g}, ${c.b}, ${typeof c.a === "number" ? c.a : 1})`
        : undefined;

/**
 * Link-Design einer Box: aus der ersten Quelle, die eins trägt — üblich
 * `(slide.options, presentationOptions.slideOptions)`. Eigene Optionen einer
 * Slide, die älter sind als das Link-Design, erben so das des Scrollys.
 * Liest beide Schreibweisen (Alt-Posts kommen teils in snake_case).
 */
export function linkStilAus(...quellen: Array<object | null | undefined>): LinkStyle | undefined {
    for (const q of quellen) {
        const roh = q as { linkStyle?: unknown; link_style?: unknown } | null | undefined;
        const stil = roh?.linkStyle ?? roh?.link_style;
        if (stil && typeof stil === "object") return stil as LinkStyle;
    }
    return undefined;
}

/** CSS-Variablen für das Box-Element; was fehlt, fällt auf die Vorgabe zurück. */
export function linkStilVariablen(stil: LinkStyle | null | undefined): Record<string, string> {
    const v: Record<string, string> = {};
    const farbe = alsCss(stil?.color);
    const besucht = alsCss(stil?.visitedColor);
    const hover = alsCss(stil?.hoverColor);
    if (farbe) v["--scrolly-link"] = farbe;
    if (besucht) v["--scrolly-link-besucht"] = besucht;
    if (hover) v["--scrolly-link-hover"] = hover;
    if (stil?.underline === "hover") {
        v["--scrolly-link-strich"] = "none";
        v["--scrolly-link-strich-hover"] = "underline";
    } else if (stil?.underline === "never") {
        v["--scrolly-link-strich"] = "none";
        v["--scrolly-link-strich-hover"] = "none";
    }
    return v;
}

/** Alle Variablennamen — zum Entfernen, wenn eine Box kein Link-Design (mehr) hat. */
export const LINK_STIL_VARIABLEN = [
    "--scrolly-link",
    "--scrolly-link-besucht",
    "--scrolly-link-hover",
    "--scrolly-link-strich",
    "--scrolly-link-strich-hover",
] as const;

/**
 * Stylesheet-Regeln für Links innerhalb von `bereich` (CSS-Selektor der Box,
 * in styled-components auch `&`). Ohne Variablen: Textfarbe + Unterstrich.
 * `currentColor` als Farbe des Links = die geerbte Textfarbe.
 */
export function linkRegeln(bereich: string): string {
    return [
        `${bereich} a { color: var(--scrolly-link, currentColor); text-decoration-line: var(--scrolly-link-strich, underline); }`,
        `${bereich} a:visited { color: var(--scrolly-link-besucht, var(--scrolly-link, currentColor)); }`,
        `${bereich} a:hover { color: var(--scrolly-link-hover, var(--scrolly-link, currentColor)); text-decoration-line: var(--scrolly-link-strich-hover, underline); }`,
    ].join("\n");
}
